// AuthModal — 鉴权弹窗
// 三种模式：登录（密码/验证码）/ 注册（邮箱+验证码+密码，无用户名）/ 找回密码
// 参考豆包/千问等智能助手 app 的居中圆角风格
// 来源: 统一管理平台api文档.txt 第 4-8 节

import { useState } from 'react';
import {
  sendCode,
  register,
  login,
  verifyLogin,
  resetPassword,
  type AuthUser,
} from '../../lib/api';
import { useAuthStore } from '../../store/authStore';
import { useToastStore } from '../common/Toast';

type Mode = 'login' | 'register' | 'reset';
type LoginSub = 'password' | 'code';

interface AuthModalProps {
  onClose: () => void;
  onAuthenticated?: (user: AuthUser) => void;
  /** 强制模式：未登录不可关闭（登录守卫用） */
  required?: boolean;
}

export function AuthModal({ onClose, onAuthenticated, required }: AuthModalProps) {
  const [mode, setMode] = useState<Mode>('login');
  const [loginSub, setLoginSub] = useState<LoginSub>('password');
  const setAuth = useAuthStore((s) => s.setAuth);
  const toast = useToastStore((s) => s.show);

  const handleSuccess = (user: AuthUser, token: string, msg: string) => {
    setAuth(user, token);
    toast(msg, 'success');
    onAuthenticated?.(user);
    onClose();
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center animate-fade-in p-4 ${
        required ? 'bg-transparent' : 'bg-ink/40 backdrop-blur-sm'
      }`}
    >
      <div className="card w-full max-w-sm p-6 animate-slide-up">
        <div className="text-center mb-5">
          <div className="text-5xl mb-2">🚤</div>
          <h2 className="text-xl font-bold text-ink">快船账号</h2>
          <p className="text-xs text-muted mt-1">登录后开启同账号设备互连</p>
        </div>

        {/* 模式切换 */}
        <div className="flex gap-1 p-1 bg-bg rounded-md mb-5">
          {([
            ['login', '登录'],
            ['register', '注册'],
            ['reset', '找回密码'],
          ] as [Mode, string][]).map(([m, label]) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${
                mode === m ? 'bg-surface text-primary shadow-card' : 'text-muted hover:text-ink'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {mode === 'login' && (
          <LoginForm
            loginSub={loginSub}
            setLoginSub={setLoginSub}
            onSuccess={handleSuccess}
          />
        )}
        {mode === 'register' && <RegisterForm onSuccess={handleSuccess} />}
        {mode === 'reset' && <ResetForm onDone={() => setMode('login')} />}

        {!required && (
          <button type="button" onClick={onClose} className="btn-ghost w-full mt-4">
            取消
          </button>
        )}
        {required && (
          <p className="text-xs text-muted text-center mt-4">登录后即可使用快船全部功能</p>
        )}
      </div>
    </div>
  );
}

// ===== 登录 =====
function LoginForm({
  loginSub,
  setLoginSub,
  onSuccess,
}: {
  loginSub: LoginSub;
  setLoginSub: (s: LoginSub) => void;
  onSuccess: (user: AuthUser, token: string, msg: string) => void;
}) {
  const toast = useToastStore((s) => s.show);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!email) return toast('请输入邮箱', 'warning');
    setLoading(true);
    try {
      if (loginSub === 'password') {
        if (!password) return toast('请输入密码', 'warning');
        const r = await login(email, password);
        if (r.success && r.user && r.token) {
          onSuccess(r.user, r.token, '登录成功');
        } else {
          toast(r.message ?? '登录失败', 'error');
        }
      } else {
        if (!code) return toast('请输入验证码', 'warning');
        const r = await verifyLogin(email, code);
        if (r.success && r.user && r.token) {
          onSuccess(r.user, r.token, r.isNewUser ? '已自动注册并登录' : '登录成功');
        } else {
          toast(r.message ?? '登录失败', 'error');
        }
      }
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <input className="input" type="email" placeholder="邮箱" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
      {loginSub === 'password' ? (
        <input className="input" type="password" placeholder="密码" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
      ) : (
        <SendCodeRow email={email} onDone={(c) => setCode(c)} value={code} />
      )}
      <button type="button" onClick={submit} disabled={loading} className="btn-primary w-full">
        {loading ? '登录中…' : '登录'}
      </button>
      <button
        type="button"
        onClick={() => setLoginSub(loginSub === 'password' ? 'code' : 'password')}
        className="text-xs text-muted hover:text-primary w-full text-center"
      >
        {loginSub === 'password' ? '切换验证码登录（免密码）' : '切换密码登录'}
      </button>
    </div>
  );
}

// ===== 注册（邮箱+验证码+密码，无用户名/无昵称）=====
function RegisterForm({ onSuccess }: { onSuccess: (user: AuthUser, token: string, msg: string) => void }) {
  const toast = useToastStore((s) => s.show);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!email) return toast('请输入邮箱', 'warning');
    if (!code) return toast('请输入验证码', 'warning');
    if (password.length < 6) return toast('密码至少 6 位', 'warning');
    setLoading(true);
    try {
      const r = await register(email, code, password);
      if (r.success && r.user && r.token) {
        onSuccess(r.user, r.token, '注册成功');
      } else {
        toast(r.message ?? '注册失败', 'error');
      }
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <input className="input" type="email" placeholder="邮箱" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
      <SendCodeRow email={email} onDone={(c) => setCode(c)} value={code} />
      <input className="input" type="password" placeholder="密码（至少 6 位）" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
      <button type="button" onClick={submit} disabled={loading} className="btn-primary w-full">
        {loading ? '注册中…' : '注册'}
      </button>
      <p className="text-xs text-muted text-center">仅需邮箱 + 验证码 + 密码，注册即开通统一账号</p>
    </div>
  );
}

// ===== 找回密码 =====
function ResetForm({ onDone }: { onDone: () => void }) {
  const toast = useToastStore((s) => s.show);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!email) return toast('请输入邮箱', 'warning');
    if (!code) return toast('请输入验证码', 'warning');
    if (newPassword.length < 6) return toast('密码至少 6 位', 'warning');
    setLoading(true);
    try {
      const r = await resetPassword(email, code, newPassword);
      if (r.success) {
        toast('密码重置成功，请登录', 'success');
        onDone();
      } else {
        toast(r.message ?? '重置失败', 'error');
      }
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <input className="input" type="email" placeholder="邮箱" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
      <SendCodeRow email={email} onDone={(c) => setCode(c)} value={code} />
      <input className="input" type="password" placeholder="新密码（至少 6 位）" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" />
      <button type="button" onClick={submit} disabled={loading} className="btn-primary w-full">
        {loading ? '重置中…' : '重置密码'}
      </button>
    </div>
  );
}

// ===== 发送验证码行（含 60s 倒计时）=====
function SendCodeRow({
  email,
  value,
  onDone,
}: {
  email: string;
  value: string;
  onDone: (code: string) => void;
}) {
  const toast = useToastStore((s) => s.show);
  const [countdown, setCountdown] = useState(0);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!email) return toast('请先输入邮箱', 'warning');
    if (countdown > 0 || sending) return;
    setSending(true);
    try {
      const r = await sendCode(email);
      if (r.success) {
        toast('验证码已发送，5 分钟内有效', 'success');
        setCountdown(60);
        const timer = setInterval(() => {
          setCountdown((c) => {
            if (c <= 1) {
              clearInterval(timer);
              return 0;
            }
            return c - 1;
          });
        }, 1000);
      } else {
        toast(r.message ?? '发送失败', 'error');
      }
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex gap-2">
      <input
        className="input flex-1"
        type="text"
        inputMode="numeric"
        placeholder="6 位验证码"
        value={value}
        onChange={(e) => onDone(e.target.value.replace(/\D/g, ''))}
        maxLength={6}
      />
      <button
        type="button"
        onClick={handleSend}
        disabled={countdown > 0 || sending}
        className="btn-outline px-3 whitespace-nowrap"
      >
        {countdown > 0 ? `${countdown}s` : sending ? '发送中' : '发送'}
      </button>
    </div>
  );
}
